import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch } from './apiClient';
import { useAuthContext } from './AuthContext';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'queued', label: 'Queued' },
  { id: 'approved', label: 'Approved' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'rejected', label: 'Rejected' },
];

function badgeStyle(background, color) {
  return {
    background,
    color,
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: '0.72rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
}

function severityBadge(severity) {
  switch (severity) {
    case 'critical':
      return badgeStyle('#fef2f2', '#991b1b');
    case 'high':
      return badgeStyle('#fff7ed', '#9a3412');
    case 'low':
      return badgeStyle('#ecfdf5', '#166534');
    default:
      return badgeStyle('#eff6ff', '#1d4ed8');
  }
}

function dispositionBadge(disposition) {
  switch (disposition) {
    case 'auto_fix_candidate':
      return badgeStyle('#ede9fe', '#5b21b6');
    case 'auto_feature_candidate':
      return badgeStyle('#ecfccb', '#3f6212');
    default:
      return badgeStyle('#f3f4f6', '#374151');
  }
}

function referenceStatusBadge(status) {
  switch (status) {
    case 'approved':
      return badgeStyle('#ecfdf5', '#166534');
    case 'rejected':
      return badgeStyle('#fef2f2', '#991b1b');
    case 'recommended':
      return badgeStyle('#eff6ff', '#1d4ed8');
    default:
      return badgeStyle('#f3f4f6', '#374151');
  }
}

export default function FeedbackReviewPage() {
  const { user, loading } = useAuthContext();
  const [filter, setFilter] = useState('queued');
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [savingTicketId, setSavingTicketId] = useState(null);
  const [notesDrafts, setNotesDrafts] = useState({});

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;

    async function loadQueue() {
      setLoadingTickets(true);
      setError('');
      try {
        const query = filter === 'all' ? '' : `?reviewStatus=${encodeURIComponent(filter)}`;
        const response = await apiGet(`/v1/feedback/review-queue${query}`);
        if (!cancelled) {
          setTickets(response.tickets || []);
          setNotesDrafts(Object.fromEntries((response.tickets || []).map((ticket) => [ticket.ticketId, ticket.ownerNotes || ''])));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoadingTickets(false);
      }
    }

    loadQueue();
    return () => { cancelled = true; };
  }, [filter, loading, user]);

  const stats = useMemo(() => ({
    queued: tickets.filter((ticket) => ticket.reviewStatus === 'queued').length,
    approved: tickets.filter((ticket) => ticket.reviewStatus === 'approved').length,
    autoReady: tickets.filter((ticket) => ticket.autoDisposition !== 'owner_review_required').length,
  }), [tickets]);

  async function updateTicket(ticketId, payload) {
    setSavingTicketId(ticketId);
    setError('');
    try {
      const response = await apiPatch(`/v1/feedback/review-queue/${ticketId}`, payload);
      setTickets((current) => current.map((ticket) => (
        ticket.ticketId === ticketId ? response.ticket : ticket
      )));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTicketId(null);
    }
  }

  if (loading || !user) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ color: '#111827', marginBottom: 12 }}>Review Queue</h1>
        <p style={{ color: '#6b7280' }}>Sign in to review published product feedback.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px 80px' }}>
        <Link to="/account" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' }}>
          ← Back to Account
        </Link>

        <div style={{ marginTop: 18, marginBottom: 28 }}>
          <h1 style={{ color: '#111827', margin: 0, fontSize: '2rem', fontWeight: 800 }}>Feedback Review Queue</h1>
          <p style={{ color: '#6b7280', marginTop: 10, lineHeight: 1.6, maxWidth: 760 }}>
            Review feedback for products you own or maintain, decide what should be queued for AI iteration,
            and keep the public remix loop healthy without waiting on an admin pass.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 18 }}>
            <div style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>Queued</div>
            <div style={{ color: '#111827', fontSize: '1.8rem', fontWeight: 800, marginTop: 6 }}>{stats.queued}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 18 }}>
            <div style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>Approved</div>
            <div style={{ color: '#111827', fontSize: '1.8rem', fontWeight: 800, marginTop: 6 }}>{stats.approved}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 18 }}>
            <div style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>Auto-ready</div>
            <div style={{ color: '#111827', fontSize: '1.8rem', fontWeight: 800, marginTop: 6 }}>{stats.autoReady}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {FILTERS.map((item) => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id)}
              style={{
                border: filter === item.id ? '1px solid #1d4ed8' : '1px solid #d1d5db',
                background: filter === item.id ? '#dbeafe' : '#fff',
                color: filter === item.id ? '#1d4ed8' : '#374151',
                borderRadius: 999,
                padding: '8px 14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: 18, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 12, padding: '14px 16px' }}>
            {error}
          </div>
        )}

        {loadingTickets ? (
          <div style={{ color: '#6b7280', padding: '60px 0', textAlign: 'center' }}>Loading review queue…</div>
        ) : tickets.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, padding: '48px 24px', textAlign: 'center' }}>
            <h2 style={{ color: '#111827', marginBottom: 8 }}>Nothing waiting right now</h2>
            <p style={{ color: '#6b7280', margin: 0 }}>New Discover feedback will show up here once it lands for products you own or maintain.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {tickets.map((ticket) => {
              const notesValue = notesDrafts[ticket.ticketId] ?? ticket.ownerNotes ?? '';
              const queueAction = ticket.type === 'bug' ? 'fix' : 'feature';
              const externalReferences = ticket.product?.sourceBundle?.externalReferences || [];
              const feedbackAutomation = ticket.product?.feedbackAutomation || {};
              return (
                <div key={ticket.ticketId} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, padding: 22, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 280 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        <span style={severityBadge(ticket.severity)}>{ticket.severity}</span>
                        <span style={badgeStyle('#f3f4f6', '#111827')}>{ticket.type}</span>
                        <span style={dispositionBadge(ticket.autoDisposition)}>
                          {ticket.autoDisposition.replaceAll('_', ' ')}
                        </span>
                        <span style={badgeStyle('#eff6ff', '#1d4ed8')}>{ticket.reviewStatus}</span>
                      </div>
                      <h3 style={{ color: '#111827', margin: '0 0 6px', fontSize: '1.15rem' }}>{ticket.title}</h3>
                      <div style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: 10 }}>
                        {ticket.product?.name || 'Unknown Product'} · {ticket.product?.publisherName || 'Unknown Publisher'} · {ticket.reviewerRole}
                      </div>
                      <p style={{ color: '#374151', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
                    </div>

                    <div style={{ minWidth: 240, flex: '0 0 260px' }}>
                      <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                        <div style={{ color: '#64748b', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
                          Automatic Read
                        </div>
                        <div style={{ color: '#111827', fontSize: '0.92rem', fontWeight: 700, marginBottom: 6 }}>
                          {(ticket.autoDispositionConfidence || 0).toFixed(2)} confidence
                        </div>
                        <div style={{ color: '#475569', fontSize: '0.88rem', lineHeight: 1.5 }}>
                          {ticket.autoDispositionReason || 'No automatic assessment available yet.'}
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                        <div style={{ color: '#64748b', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
                          Feedback Automation
                        </div>
                        <div style={{ color: '#111827', fontSize: '0.9rem', lineHeight: 1.6 }}>
                          Auto-fix: <strong>{feedbackAutomation.autoQueueFixes ? 'On' : 'Off'}</strong><br />
                          Auto-feature: <strong>{feedbackAutomation.autoQueueFeatures ? 'On' : 'Off'}</strong><br />
                          Threshold: <strong>{Number(feedbackAutomation.minimumConfidence || 0.84).toFixed(2)}</strong><br />
                          Job status: <strong>{ticket.automationStatus || 'not_requested'}</strong>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => updateTicket(ticket.ticketId, {
                            decision: 'approve',
                            queueForAutomation: true,
                            automationRequestedAction: queueAction,
                            ownerNotes: notesValue,
                          })}
                          disabled={savingTicketId === ticket.ticketId}
                          style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Queue for AI
                        </button>
                        <button
                          onClick={() => updateTicket(ticket.ticketId, {
                            decision: 'approve',
                            ownerNotes: notesValue,
                          })}
                          disabled={savingTicketId === ticket.ticketId}
                          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateTicket(ticket.ticketId, {
                            decision: 'reject',
                            ownerNotes: notesValue,
                          })}
                          disabled={savingTicketId === ticket.ticketId}
                          style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => updateTicket(ticket.ticketId, {
                            decision: 'resolve',
                            ownerNotes: notesValue,
                          })}
                          disabled={savingTicketId === ticket.ticketId}
                          style={{ background: '#ecfdf5', color: '#166534', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Resolve
                        </button>
                      </div>
                    </div>
                  </div>

                  {externalReferences.length > 0 && (
                    <div style={{ marginTop: 18, padding: 16, borderRadius: 14, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
                      <div style={{ color: '#111827', fontWeight: 800, marginBottom: 10 }}>Reviewed References</div>
                      <div style={{ display: 'grid', gap: 12 }}>
                        {externalReferences.map((reference) => (
                          <div key={reference.url} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 220 }}>
                                <a href={reference.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>
                                  {reference.title}
                                </a>
                                <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 4 }}>
                                  {(reference.host || 'reference')} · {reference.sourceType || 'other'}
                                  {reference.license ? ` · ${reference.license}` : ''}
                                </div>
                                {reference.summary && (
                                  <p style={{ color: '#475569', fontSize: '0.88rem', lineHeight: 1.5, margin: '8px 0 0' }}>
                                    {reference.summary}
                                  </p>
                                )}
                              </div>
                              <span style={referenceStatusBadge(reference.reviewStatus || 'discovered')}>
                                {(reference.reviewStatus || 'discovered').replaceAll('_', ' ')}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                              <button
                                onClick={() => updateTicket(ticket.ticketId, {
                                  ownerNotes: notesValue,
                                  externalReferenceUpdates: [{ url: reference.url, reviewStatus: 'approved' }],
                                })}
                                disabled={savingTicketId === ticket.ticketId}
                                style={{ background: '#ecfdf5', color: '#166534', border: '1px solid #86efac', borderRadius: 10, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
                              >
                                Approve reference
                              </button>
                              <button
                                onClick={() => updateTicket(ticket.ticketId, {
                                  ownerNotes: notesValue,
                                  externalReferenceUpdates: [{ url: reference.url, reviewStatus: 'recommended' }],
                                })}
                                disabled={savingTicketId === ticket.ticketId}
                                style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
                              >
                                Recommend
                              </button>
                              <button
                                onClick={() => updateTicket(ticket.ticketId, {
                                  ownerNotes: notesValue,
                                  externalReferenceUpdates: [{ url: reference.url, reviewStatus: 'rejected' }],
                                })}
                                disabled={savingTicketId === ticket.ticketId}
                                style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 10, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
                              >
                                Reject reference
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
                        Owner Notes
                      </label>
                      <textarea
                        value={notesValue}
                        onChange={(event) => setNotesDrafts((current) => ({ ...current, [ticket.ticketId]: event.target.value }))}
                        rows={3}
                        style={{ width: '100%', borderRadius: 12, border: '1px solid #d1d5db', padding: 12, fontFamily: 'inherit', fontSize: '0.92rem', resize: 'vertical' }}
                        placeholder="Why is this valid or not? What should AIWizard do next?"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {ticket.product?.slug && (
                        <Link
                          to={`/hub/${ticket.product.slug}`}
                          style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}
                        >
                          View product
                        </Link>
                      )}
                      {ticket.previewURL && (
                        <a href={ticket.previewURL} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>
                          Open preview
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
